// MapEditor component - Main editor canvas for adventure creation

import { css, html } from 'lit';
import { GameEventsController } from '../../controllers/game-events-controller.js';
import { InteractionStateController } from '../../controllers/interaction-state-controller.js';
import { MapStateController } from '../../controllers/map-state-controller.js';
import { ViewStateController } from '../../controllers/view-state-controller.js';
import { authState } from '../../lib/auth.js';
import { BaseComponent } from '../../lib/base-component.js';
import { loadImage } from '../../rendering/image-cache.js';
import { renderMapEditor } from '../../rendering/render-pipeline.js';
import {
  findCoverAtPosition,
  findLightAtPosition,
  findMonsterAtPosition,
  findOpeningAtPosition,
  findWallAtPosition,
  getDirectionFromAngle,
  getWallSegmentAtPoint,
  isPointInDirectionIndicator,
} from '../../rendering/selection-detection.js';
import { interactionState } from '../../state/interaction-state.js';
import { mapState } from '../../state/map-state.js';
import { editorViewState as viewState } from '../../state/view-state.js';
// @ts-expect-error - resolved via importmap at runtime

// Import UI components
import '../shared/toolbar-button.js';
import '../shared/drawer.js';
import '../shared/drawer-bar.js';
import '../shared/view-controls.js';
import '../game/rotate-cursor.js';
import '../game/movement-confirmation-buttons.js';
import '../game/pending-moves-approval-banner.js';
import './light-settings-panel.js';
import './wall-settings-panel.js';
import './opening-settings-panel.js';
import './map-settings-panel.js';
import './monster-properties-panel.js';
import './monster-library.js';

// Inline lucide icons (static, trusted SVGs; inherit currentColor).
const trashIcon = html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><path d="M10 11v6" /><path d="M14 11v6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>`;
const settingsIcon = html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" /><circle cx="12" cy="12" r="3" /></svg>`;

/**
 * MapEditor component
 * Main canvas component for adventure map editing
 */
class MapEditor extends BaseComponent {
  static properties = {
    mapId: { type: String, attribute: 'map-id' },
    adventureId: { type: String, attribute: 'adventure-id' },
    backendUrl: { type: String, attribute: 'backend-url' },
    currentUserId: { type: String, attribute: 'current-user-id' },
    width: { type: String },
    height: { type: String },
    // Internal state
    _canvasWidth: { type: Number, state: true },
    _canvasHeight: { type: Number, state: true },
    _tool: { type: String, state: true },
    _wallSubtool: { type: String, state: true },
    _openingSubtool: { type: String, state: true },
    _lightSubtool: { type: String, state: true },
    _coverSubtool: { type: String, state: true },
    _drawerOpen: { type: Boolean, state: true },
    _drawerActiveView: { type: String, state: true },
    _showGrid: { type: Boolean, state: true },
    _showWalls: { type: Boolean, state: true },
    _globalIllumination: { type: String, state: true },
    _eventsLoading: { type: Boolean, state: true },
    _isWallPanelOpen: { type: Boolean, state: true },
    _isOpeningPanelOpen: { type: Boolean, state: true },
    _isLightPanelOpen: { type: Boolean, state: true },
    _isMapPanelOpen: { type: Boolean, state: true },
    _monsterPropertiesPanelVisible: { type: Boolean, state: true },
    _selectedMonsterIndex: { type: Number, state: true },
    _monsterContextMenu: { type: Object, state: true },
    _selectedWallIndex: { type: Number, state: true },
    _selectedOpeningIndex: { type: Number, state: true },
    _selectedLightIndex: { type: Number, state: true },
    _selectedCoverIndex: { type: Number, state: true },
    _hoveredWallIndex: { type: Number, state: true },
    _hoveredOpeningIndex: { type: Number, state: true },
    _hoveredLightIndex: { type: Number, state: true },
    _hoveredCoverIndex: { type: Number, state: true },
    _hoveredMonsterIndex: { type: Number, state: true },
    _isDraggingDirection: { type: Boolean, state: true },
    _showRotateCursor: { type: Boolean, state: true },
    _rotateCursorX: { type: Number, state: true },
    _rotateCursorY: { type: Number, state: true },
    _wallPreviewPoint: { type: Object, state: true },
    _viewingAsPC: { type: String, state: true },
    _debugMode: { type: Boolean, state: true },
    saveAction: { type: Object },
    fetchMonsters: { type: Object },
  };

  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }

    .map-editor-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
    }

    .map-editor-container {
      position: relative;
      flex: 1;
      height: 100%;
      overflow: hidden;
      background-color: #1a1a2e;
    }

    .map-editor {
      display: block;
      cursor: crosshair;
    }

    .map-editor.mode-select {
      cursor: default;
    }

    .map-editor.mode-pan {
      cursor: grab;
    }

    .map-editor.mode-pan.is-panning {
      cursor: grabbing;
    }

    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 100;
    }

    .loading-spinner {
      width: 48px;
      height: 48px;
      border: 4px solid #333;
      border-top-color: #4a9eff;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .loading-text {
      margin-top: 16px;
      color: #ccc;
      font-size: 14px;
    }

    .top-banners-container {
      position: absolute;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      z-index: 20;
      pointer-events: none;
    }

    .top-banners-container > * {
      pointer-events: auto;
    }

    .debug-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      background: rgba(0, 0, 0, 0.8);
      color: #999;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-family: monospace;
    }

    .pc-starting-positions-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      background: rgba(0, 0, 0, 0.8);
      color: #22c55e;
      padding: 12px 24px;
      border-radius: 8px;
      border: 1px solid #22c55e;
      font-size: 14px;
      font-weight: 500;
    }

    .exit-pc-view-banner {
      display: flex;
      align-items: center;
      gap: 16px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      border: 1px solid #8b5cf6;
    }

    .viewing-text {
      font-size: 14px;
    }

    .exit-pc-view-button {
      display: flex;
      align-items: center;
      gap: 8px;
      background: #8b5cf6;
      border: none;
      color: white;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }

    .exit-pc-view-button:hover {
      background: #7c3aed;
    }

    .tools-sidebar {
      position: absolute;
      top: 20px;
      left: 20px;
      display: flex;
      gap: 8px;
      z-index: 10;
    }

    .tool-buttons-container {
      display: flex;
      flex-direction: row;
      gap: 12px;
    }

    .tool-buttons {
      display: flex;
      flex-direction: column;
      gap: 8px;
      background: rgba(0, 0, 0, 0.8);
      padding: 12px;
      border-radius: 8px;
      border: 1px solid #444;
      backdrop-filter: blur(4px);
    }

    /* .tool-separator styling lives on toolbar-well via ::slotted so the
       rule is direction-aware (column vs row). */

    .door-context-menu,
    .monster-context-menu {
      position: absolute;
      display: flex;
      gap: 4px;
      background: rgba(0, 0, 0, 0.9);
      padding: 8px;
      border-radius: 8px;
      border: 1px solid #555;
      z-index: 100;
      transform: translate(-50%, -100%);
    }

    .door-context-menu button,
    .monster-context-menu button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      background: #333;
      border: 1px solid #555;
      border-radius: 6px;
      color: white;
      cursor: pointer;
      transition: all 0.2s;
    }

    .door-context-menu button:hover,
    .monster-context-menu button:hover {
      background: #444;
      border-color: #666;
    }

    .monster-drag-icon {
      position: absolute;
      pointer-events: none;
      z-index: 50;
    }

    .icon-can-place {
      color: #22c55e;
    }

    .icon-cannot-place {
      color: #ef4444;
    }

    .drawer-view {
      height: 100%;
      overflow-y: auto;
    }
  `;

  constructor() {
    super();
    this.mapId = '';
    this.adventureId = '';
    this.backendUrl = '';
    this.currentUserId = '';
    this.width = '100%';
    this.height = '100%';

    // Internal state
    this._canvasWidth = 0;
    this._canvasHeight = 0;
    this._tool = 'select';
    this._wallSubtool = 'selectWall';
    this._openingSubtool = 'selectOpening';
    this._lightSubtool = 'selectLight';
    this._coverSubtool = 'selectCover';
    // Load drawer state from localStorage
    const savedDrawerOpen = localStorage.getItem('d3-gamemap-editor-drawerOpen');
    this._drawerOpen = savedDrawerOpen === 'true';
    /** @type {string|null} */
    const savedDrawerActiveView = localStorage.getItem('d3-gamemap-editor-drawerActiveView');
    this._drawerActiveView = savedDrawerActiveView || null;
    this._showGrid = true;
    this._showWalls = true;
    this._globalIllumination = 'none';
    this._eventsLoading = true;
    this._isWallPanelOpen = false;
    this._isOpeningPanelOpen = false;
    this._isLightPanelOpen = false;
    this._isMapPanelOpen = false;
    this._monsterPropertiesPanelVisible = false;
    /** @type {number|null} */
    this._selectedMonsterIndex = null;
    /** @type {{ visible: boolean, x: number, y: number, monsterIndex: number|null }} */
    this._monsterContextMenu = { visible: false, x: 0, y: 0, monsterIndex: null };
    /** @type {number|null} */
    this._selectedWallIndex = null;
    /** @type {number|null} */
    this._selectedOpeningIndex = null;
    /** @type {number|null} */
    this._selectedLightIndex = null;
    /** @type {number|null} */
    this._selectedCoverIndex = null;
    /** @type {number|null} */
    this._hoveredWallIndex = null;
    /** @type {number|null} */
    this._hoveredOpeningIndex = null;
    /** @type {number|null} */
    this._hoveredLightIndex = null;
    /** @type {number|null} */
    this._hoveredCoverIndex = null;
    /** @type {number|null} */
    this._hoveredMonsterIndex = null;
    this._isDraggingDirection = false;
    this._showRotateCursor = false;
    this._rotateCursorX = 0;
    this._rotateCursorY = 0;
    /** @type {{x: number, y: number}|null} */
    this._wallPreviewPoint = null;
    /** @type {{x: number, y: number}|null} */
    this._lightPreviewPoint = null;
    /** @type {{start: {x: number, y: number}, end: {x: number, y: number}}|null} */
    this._hoveredWallSegment = null;
    /** @type {string|null} */
    this._viewingAsPC = null;
    this._debugMode = false;

    // Reactive controllers
    this._mapState = new MapStateController(this);
    this._viewState = new ViewStateController(this, { isEditor: true });
    this._interaction = new InteractionStateController(this);
    this._gameEvents = new GameEventsController(this, {
      entityType: 'adventure',
      storagePrefix: 'editor',
    });

    /** @type {((params: import('./monster-library.js').FetchMonstersParams) => Promise<import('./monster-library.js').FetchMonstersResult>) | null} */
    this.fetchMonsters = null;
    /** @type {((action: {type: string, data: object}) => Promise<import('../../types/jsdoc-types.js').GameEvent|null>) | null} */
    this.saveAction = null;

    // Animation state
    this._animationFrameId = null;
    this._resizeObserver = null;

    // Mouse state
    this._isMouseDown = false;
    this._isRightMouseDown = false;
    this._isPanning = false;
    this._lastMouseX = 0;
    this._lastMouseY = 0;
    this._mouseGridPosition = null;

    // Drawing state
    /** @type {Array<{x: number, y: number}>|null} */
    this._currentWall = null;
    /** @type {{points: Array<{x: number, y: number}>}|null} */
    this._currentOpening = null;
    /** @type {{col: number, row: number, brightRadius: number, dimRadius: number}|null} */
    this._currentLight = null;
    /** @type {Array<{x: number, y: number}>|null} */
    this._currentCover = null;
    /** @type {{x: number, y: number}|null} */
    this._coverPreviewPoint = null;

    // Monster drag state
    this._isDraggingNewMonster = false;
    /** @type {{ monster: { monsterSlug: string, name: string, imageUrl: string, tokenSlug: string, borderColor: string, size: string }, position: { x: number, y: number }, canPlace: boolean }|null} */
    this._monsterDragPreview = null;
    /** @type {HTMLImageElement|null} */
    this._preloadedDragImage = null;

    // Existing-placement drag state (#161). On mousedown over a placement
    // we record the start; if the cursor moves past _placementDragThreshold
    // before mouseup, it becomes a drag-to-move; otherwise it's a click
    // and the context menu opens as before.
    /** @type {number|null} */
    this._draggingPlacementIndex = null;
    /** @type {{x: number, y: number}|null} */
    this._draggingPlacementStartScreen = null;
    /** @type {{col: number, row: number}|null} */
    this._draggingPlacementPreview = null;
    this._draggingPlacementStarted = false;
    this._placementDragThreshold = 5;
  }

  async connectedCallback() {
    await super.connectedCallback();
    // Controllers handle reset + subscriptions automatically
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopRenderLoop();
    // Controllers handle unsubscribe + SSE disconnect automatically
    this._cleanupResizeObserver();
  }

  firstUpdated() {
    // Set storage ID so view state persists across reloads
    if (this.mapId) {
      this._viewState.state.setStorageId(this.mapId);
    }
    this._setupCanvas();
    this._setupResizeObserver();
    this._setupEventListeners();
    this._startRenderLoop();
    this._connectSSE();
  }

  _setupCanvas() {
    const container = this.shadowRoot?.querySelector('.map-editor-container');
    const canvas = this.shadowRoot?.querySelector('canvas');
    if (!container || !canvas) return;

    const rect = container.getBoundingClientRect();
    this._canvasWidth = rect.width;
    this._canvasHeight = rect.height;
    canvas.width = rect.width;
    canvas.height = rect.height;

    this._canvas = canvas;
    this._ctx = canvas.getContext('2d');
    this._container = container;
  }

  _setupResizeObserver() {
    const container = this._container;
    if (!container) return;

    this._resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        this._canvasWidth = width;
        this._canvasHeight = height;
        if (this._canvas) {
          this._canvas.width = width;
          this._canvas.height = height;
        }
      }
    });

    this._resizeObserver.observe(container);
  }

  _cleanupResizeObserver() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }

  _setupEventListeners() {
    const canvas = this._canvas;
    if (!canvas) return;

    canvas.addEventListener('mousedown', this._handleMouseDown.bind(this));
    canvas.addEventListener('contextmenu', this._handleContextMenu.bind(this));
    canvas.addEventListener('wheel', this._handleWheel.bind(this), { passive: false });
    // Use window for mouseup/mousemove so drag from drawer works outside canvas
    window.addEventListener('mouseup', this._handleMouseUp.bind(this));
    window.addEventListener('mousemove', this._handleMouseMove.bind(this));
    window.addEventListener('keydown', this._handleKeyDown.bind(this));
  }

  _connectSSE() {
    if (!this.adventureId || this.backendUrl === undefined) return;

    this._gameEvents.connect({
      entityId: this.adventureId,
      backendUrl: this.backendUrl,
      getAccessToken: () => authState.getAccessToken(),
      callbacks: {
        onConnected: () => {
          console.debug('[MapEditor] SSE connected');
          this._eventsLoading = false;
        },
        onDisconnected: () => {
          console.debug('[MapEditor] SSE disconnected');
        },
        onError: (/** @type {Event} */ error) => {
          console.error('[MapEditor] SSE error:', error);
        },
      },
    });
  }

  _startRenderLoop() {
    const render = () => {
      this._renderCanvas();
      this._animationFrameId = requestAnimationFrame(render);
    };
    this._animationFrameId = requestAnimationFrame(render);
  }

  _stopRenderLoop() {
    if (this._animationFrameId) {
      cancelAnimationFrame(this._animationFrameId);
      this._animationFrameId = null;
    }
  }

  _renderCanvas() {
    const ctx = this._ctx;
    if (!ctx) return;

    const width = this._canvasWidth;
    const height = this._canvasHeight;
    if (width === 0 || height === 0) return;

    renderMapEditor({
      ctx,
      canvasWidth: width,
      canvasHeight: height,
      showGrid: this._showGrid,
      showWalls: this._showWalls,
      tool: this._tool,
      mouseGridPosition: this._mouseGridPosition,
      selectedWallIndex: this._selectedWallIndex,
      hoveredWallIndex: this._hoveredWallIndex,
      currentWall: this._currentWall,
      wallPreviewPoint: this._wallPreviewPoint,
      selectedOpeningIndex: this._selectedOpeningIndex,
      hoveredOpeningIndex: this._hoveredOpeningIndex,
      selectedLightIndex: this._selectedLightIndex,
      hoveredLightIndex: this._hoveredLightIndex,
      selectedCoverIndex: this._selectedCoverIndex,
      hoveredCoverIndex: this._hoveredCoverIndex,
      currentCover: this._currentCover,
      coverPreviewPoint: this._coverPreviewPoint,
      coverSubtool: this._coverSubtool,
      lightPreviewPoint: this._lightPreviewPoint,
      lightSubtool: this._lightSubtool,
      hoveredWallSegment: this._hoveredWallSegment,
      openingSubtool: this._openingSubtool,
      viewStateOverride: viewState,
      selectedMonsterIndex: this._selectedMonsterIndex,
      hoveredMonsterIndex: this._hoveredMonsterIndex,
      placementDragOverride:
        this._draggingPlacementStarted && this._draggingPlacementIndex !== null && this._draggingPlacementPreview
          ? {
              id: mapState.monsterPlacements[this._draggingPlacementIndex]?.id,
              col: this._draggingPlacementPreview.col,
              row: this._draggingPlacementPreview.row,
            }
          : null,
    });

    // Draw monster drag preview on top of everything
    if (this._monsterDragPreview) {
      const { monster: dragMonster, position, canPlace } = this._monsterDragPreview;
      const sizeMultiplier = this._getMonsterSizeMultiplier(dragMonster.size);
      const monsterSize = mapState.gridSize * sizeMultiplier;
      const monsterRadius = monsterSize / 2;
      const { zoom, panX, panY } = viewState;

      ctx.save();
      ctx.setTransform(zoom, 0, 0, zoom, panX, panY);
      ctx.globalAlpha = canPlace ? 0.7 : 0.4;
      ctx.beginPath();
      ctx.arc(position.x, position.y, monsterRadius, 0, 2 * Math.PI);
      ctx.clip();

      const img = this._preloadedDragImage;
      if (img?.complete && img.naturalWidth > 0) {
        ctx.drawImage(img, position.x - monsterRadius, position.y - monsterRadius, monsterSize, monsterSize);
      } else {
        // Fallback: colored circle with initial
        ctx.fillStyle = '#4a9eff';
        ctx.fillRect(position.x - monsterRadius, position.y - monsterRadius, monsterSize, monsterSize);
        const fontSize = monsterSize * 0.4;
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(dragMonster.name.charAt(0).toUpperCase(), position.x, position.y);
      }
      ctx.restore();
    }
  }

  // Mouse handlers
  /** @param {MouseEvent} e */
  _handleMouseDown(e) {
    this._isMouseDown = true;
    this._lastMouseX = e.clientX;
    this._lastMouseY = e.clientY;

    if (e.button === 2) {
      this._isRightMouseDown = true;

      // Handle drawWall tool - right click completes wall
      if (this._tool === 'drawWall' && this._currentWall && this._currentWall.length > 0) {
        const worldPos = this._screenToWorld(e.clientX, e.clientY);
        const gridPoint = this._snapToGridIntersection(worldPos);
        const lastPoint = this._currentWall[this._currentWall.length - 1];
        if (lastPoint && (lastPoint.x !== gridPoint.x || lastPoint.y !== gridPoint.y)) {
          const finalPoints = [...this._currentWall, gridPoint];
          this._finishDrawingWall(finalPoints);
        } else if (this._currentWall.length >= 2) {
          this._finishDrawingWall(this._currentWall);
        }
        return;
      }

      this._isPanning = true;
      return;
    }

    const worldPos = this._screenToWorld(e.clientX, e.clientY);
    const gridCol = Math.floor(worldPos.x / mapState.gridSize);
    const gridRow = Math.floor(worldPos.y / mapState.gridSize);

    // Handle different tools
    switch (this._tool) {
      case 'pan':
        this._isPanning = true;
        break;
      case 'select':
        this._handleSelectToolClick(worldPos, gridCol, gridRow);
        break;
      case 'drawWall':
        this._handleWallToolClick(worldPos, gridCol, gridRow);
        break;
      case 'drawOpening':
        this._handleOpeningToolClick(worldPos, gridCol, gridRow);
        break;
      case 'drawLight':
        this._handleLightToolClick(worldPos, gridCol, gridRow);
        break;
      case 'drawCover':
        this._handleCoverToolClick(worldPos, gridCol, gridRow);
        break;
      case 'setPCStartingPositions':
        this._handlePCStartingPositionClick(gridCol, gridRow);
        break;
    }
  }

  /** @param {MouseEvent} e */
  async _handleMouseUp(e) {
    this._isMouseDown = false;

    if (e.button === 2) {
      this._isRightMouseDown = false;
    }

    // Complete direction drag
    if (e.button === 0 && this._isDraggingDirection && this._selectedMonsterIndex !== null) {
      const monster = mapState.monsterPlacements[this._selectedMonsterIndex];
      const newDirection = interactionState.directionDragPreview;
      if (monster && newDirection && newDirection !== (monster.direction || 'down') && this.saveAction) {
        const event = await this.saveAction({
          type: 'monsterPlacement:update',
          data: { mapId: this.mapId, id: monster.id, direction: newDirection },
        });
        if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
      }
      interactionState.endDirectionDrag();
      this._isDraggingDirection = false;
      this._showRotateCursor = false;
      if (this._canvas) this._canvas.style.cursor = '';
      return;
    }

    if (this._isPanning) {
      this._isPanning = false;
    }

    // Finish an existing-placement drag-to-move, OR — if it never crossed
    // the click threshold — open the context menu as a click would have.
    if (e.button === 0 && this._draggingPlacementIndex !== null) {
      const idx = this._draggingPlacementIndex;
      const monster = mapState.monsterPlacements[idx];
      const started = this._draggingPlacementStarted;
      const preview = this._draggingPlacementPreview;
      this._draggingPlacementIndex = null;
      this._draggingPlacementStartScreen = null;
      this._draggingPlacementPreview = null;
      this._draggingPlacementStarted = false;
      if (this._canvas) this._canvas.style.cursor = '';

      if (!started) {
        // True click — open the context menu like before.
        this._monsterContextMenu = { visible: true, x: 0, y: 0, monsterIndex: idx };
        return;
      }
      if (monster && preview && (preview.col !== monster.col || preview.row !== monster.row) && this.saveAction) {
        const event = await this.saveAction({
          type: 'monsterPlacement:move',
          data: { mapId: this.mapId, id: monster.id, toCol: preview.col, toRow: preview.row },
        });
        if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
      }
      return;
    }

    // Handle placing a new monster from monster library
    if (e.button === 0 && this._isDraggingNewMonster && this._monsterDragPreview) {
      if (this._monsterDragPreview.canPlace && this.saveAction) {
        const { monster: dragMonster, position } = this._monsterDragPreview;
        const sizeMultiplier = this._getMonsterSizeMultiplier(dragMonster.size);
        const col = Math.round(position.x / mapState.gridSize - sizeMultiplier / 2);
        const row = Math.round(position.y / mapState.gridSize - sizeMultiplier / 2);

        const event = await this.saveAction({
          type: 'monsterPlacement:add',
          data: {
            mapId: this.mapId,
            col,
            row,
            direction: 'down',
            size: dragMonster.size || 'medium',
            tokenSlug: dragMonster.tokenSlug || null,
            label: dragMonster.name,
            hidden: false,
            placementType: 'enemy',
            monsterSlug: dragMonster.monsterSlug,
          },
        });
        if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
      }
      this._isDraggingNewMonster = false;
      this._monsterDragPreview = null;
      this._preloadedDragImage = null;
      return;
    }
  }

  /** @param {MouseEvent} e */
  _handleMouseMove(e) {
    const worldPos = this._screenToWorld(e.clientX, e.clientY);
    const gridCol = Math.floor(worldPos.x / mapState.gridSize);
    const gridRow = Math.floor(worldPos.y / mapState.gridSize);

    this._mouseGridPosition = { col: gridCol, row: gridRow };

    // Handle dragging a new monster from monster library
    if (this._isDraggingNewMonster && this._monsterDragPreview) {
      const sizeMultiplier = this._getMonsterSizeMultiplier(this._monsterDragPreview.monster.size);
      const snapOffset = mapState.gridSize * (sizeMultiplier / 2);
      const snappedPos = {
        x: gridCol * mapState.gridSize + snapOffset,
        y: gridRow * mapState.gridSize + snapOffset,
      };
      this._monsterDragPreview = { ...this._monsterDragPreview, position: snappedPos };
      this._lastMouseX = e.clientX;
      this._lastMouseY = e.clientY;
      return;
    }

    if (this._isPanning) {
      const dx = e.clientX - this._lastMouseX;
      const dy = e.clientY - this._lastMouseY;
      viewState.setPan(viewState.panX + dx, viewState.panY + dy);
    }

    this._lastMouseX = e.clientX;
    this._lastMouseY = e.clientY;

    // Update hover detection based on active tool
    if (this._tool === 'drawWall') {
      if (this._wallSubtool === 'selectWall') {
        this._hoveredWallIndex = findWallAtPosition(worldPos, mapState.walls, mapState.gridSize, viewState.zoom);
        this._wallPreviewPoint = null;
      } else {
        this._hoveredWallIndex = null;
      }

      // Show wall preview point when in draw subtool
      if (this._hoveredWallIndex === null && this._wallSubtool === 'drawWall') {
        const gridSize = mapState.gridSize;
        const snapped = this._snapToGridIntersection(worldPos);
        this._wallPreviewPoint = { x: snapped.x * gridSize, y: snapped.y * gridSize };
      } else {
        this._wallPreviewPoint = null;
      }
    } else if (this._tool === 'drawOpening') {
      // Check if hovering over an existing opening (in any subtool)
      this._hoveredOpeningIndex = findOpeningAtPosition(worldPos, mapState.openings, mapState.gridSize, viewState.zoom);

      // Check for wall segments to place openings on (only when not in select mode and not hovering an opening)
      if (this._openingSubtool !== 'selectOpening' && this._hoveredOpeningIndex === null) {
        this._hoveredWallSegment = getWallSegmentAtPoint(worldPos, mapState.walls, mapState.gridSize, viewState.zoom);
      } else {
        this._hoveredWallSegment = null;
      }
    } else if (this._tool === 'drawLight') {
      if (this._lightSubtool === 'selectLight') {
        this._hoveredLightIndex = findLightAtPosition(worldPos, mapState.lights, mapState.gridSize, viewState.zoom);
      } else {
        this._hoveredLightIndex = null;
      }

      // Show preview point in drawLight subtool when not hovering a light
      if (this._lightSubtool === 'drawLight' && this._hoveredLightIndex === null) {
        const gridSize = mapState.gridSize;
        // Snap to grid center (lights go in center of cells, not intersections)
        const col = Math.floor(worldPos.x / gridSize);
        const row = Math.floor(worldPos.y / gridSize);
        this._lightPreviewPoint = { x: col * gridSize + gridSize / 2, y: row * gridSize + gridSize / 2 };
      } else {
        this._lightPreviewPoint = null;
      }
    } else if (this._tool === 'drawCover') {
      if (this._coverSubtool === 'selectCover') {
        this._hoveredCoverIndex = findCoverAtPosition(worldPos, mapState.covers, mapState.gridSize, viewState.zoom);
      } else {
        this._hoveredCoverIndex = null;
      }

      // Show cover preview point when in draw subtool and not hovering a cover
      if (this._hoveredCoverIndex === null && this._coverSubtool === 'drawCover') {
        const gridSize = mapState.gridSize;
        const snapped = this._snapToGridIntersection(worldPos);
        this._coverPreviewPoint = { x: snapped.x * gridSize, y: snapped.y * gridSize };
      } else {
        this._coverPreviewPoint = null;
      }
    } else if (this._tool === 'select') {
      // Existing-placement drag in progress — once the cursor leaves
      // the click threshold, treat as a move (#161). Show a snapped
      // preview at the hovered cell.
      if (this._draggingPlacementIndex !== null && this._draggingPlacementStartScreen) {
        const dx = e.clientX - this._draggingPlacementStartScreen.x;
        const dy = e.clientY - this._draggingPlacementStartScreen.y;
        if (!this._draggingPlacementStarted && Math.hypot(dx, dy) >= this._placementDragThreshold) {
          this._draggingPlacementStarted = true;
          if (this._canvas) this._canvas.style.cursor = 'grabbing';
        }
        if (this._draggingPlacementStarted) {
          const monster = mapState.monsterPlacements[this._draggingPlacementIndex];
          if (monster) {
            const sizeMultiplier = this._getMonsterSizeMultiplier(monster.size || 'medium');
            const col = Math.round(worldPos.x / mapState.gridSize - sizeMultiplier / 2);
            const row = Math.round(worldPos.y / mapState.gridSize - sizeMultiplier / 2);
            this._draggingPlacementPreview = { col, row };
          }
        }
        return;
      }

      // Direction drag in progress — update preview
      if (this._isDraggingDirection && this._selectedMonsterIndex !== null) {
        const monster = mapState.monsterPlacements[this._selectedMonsterIndex];
        if (monster) {
          const gridSize = mapState.gridSize;
          const sizeMultiplier = this._getMonsterSizeMultiplier(monster.size || 'medium');
          const tokenRadius = (gridSize * sizeMultiplier) / 2;
          const centerX = monster.col * gridSize + tokenRadius;
          const centerY = monster.row * gridSize + tokenRadius;
          const angle = Math.atan2(worldPos.y - centerY, worldPos.x - centerX);
          interactionState.updateDirectionDragPreview(getDirectionFromAngle(angle));
          this._showRotateCursor = true;
          this._rotateCursorX = e.clientX;
          this._rotateCursorY = e.clientY;
          if (this._canvas) this._canvas.style.cursor = 'none';
        }
        return;
      }

      // Monster hover detection
      const hoveredIdx = findMonsterAtPosition(worldPos, mapState.monsterPlacements, mapState.gridSize);
      this._hoveredMonsterIndex = hoveredIdx;

      if (hoveredIdx !== null) {
        const hoveredMonster = mapState.monsterPlacements[hoveredIdx];
        if (hoveredMonster && isPointInDirectionIndicator(worldPos, hoveredMonster, mapState.gridSize)) {
          this._showRotateCursor = true;
          this._rotateCursorX = e.clientX;
          this._rotateCursorY = e.clientY;
          if (this._canvas) this._canvas.style.cursor = 'none';
        } else {
          this._showRotateCursor = false;
          if (this._canvas) this._canvas.style.cursor = 'grab';
        }
      } else {
        this._showRotateCursor = false;
        if (this._canvas) this._canvas.style.cursor = '';
      }
    }
  }

  /** @param {MouseEvent} e */
  _handleContextMenu(e) {
    e.preventDefault();
  }

  /** @param {WheelEvent} e */
  _handleWheel(e) {
    e.preventDefault();

    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.min(4.0, Math.max(0.25, viewState.zoom + delta));

    if (!this._canvas) return;
    const rect = this._canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const worldX = (mouseX - viewState.panX) / viewState.zoom;
    const worldY = (mouseY - viewState.panY) / viewState.zoom;

    const newPanX = mouseX - worldX * newZoom;
    const newPanY = mouseY - worldY * newZoom;

    viewState.setZoom(newZoom);
    viewState.setPan(newPanX, newPanY);
  }

  /** @param {KeyboardEvent} e */
  async _handleKeyDown(e) {
    if (e.key === 'Escape') {
      this._cancelCurrentDrawing();
      this._closeAllPanels();
    }

    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (this._tool === 'select' && this._selectedMonsterIndex !== null) {
        await this._handleDeleteMonster();
        e.preventDefault();
      } else if (this._tool === 'drawWall' && this._selectedWallIndex !== null) {
        await this._handleDeleteSelectedWall();
        e.preventDefault();
      } else if (this._tool === 'drawOpening' && this._selectedOpeningIndex !== null) {
        await this._handleDeleteSelectedOpening();
        e.preventDefault();
      } else if (this._tool === 'drawLight' && this._selectedLightIndex !== null) {
        await this._handleDeleteSelectedLight();
        e.preventDefault();
      } else if (this._tool === 'drawCover' && this._selectedCoverIndex !== null) {
        await this._handleDeleteSelectedCover();
        e.preventDefault();
      }
    }
  }

  /**
   * @param {number} screenX
   * @param {number} screenY
   * @returns {{x: number, y: number}}
   */
  _screenToWorld(screenX, screenY) {
    const rect = this._canvas?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };

    const x = (screenX - rect.left - viewState.panX) / viewState.zoom;
    const y = (screenY - rect.top - viewState.panY) / viewState.zoom;
    return { x, y };
  }

  /**
   * Snap world coordinates to nearest grid intersection, returning grid coordinates
   * @param {{x: number, y: number}} worldPos
   * @returns {{x: number, y: number}}
   */
  _snapToGridIntersection(worldPos) {
    const gridSize = mapState.gridSize;
    return {
      x: Math.round(worldPos.x / gridSize),
      y: Math.round(worldPos.y / gridSize),
    };
  }

  // Tool handlers
  /**
   * @param {{x: number, y: number}} worldPos
   * @param {number} _gridCol
   * @param {number} _gridRow
   */
  _handleSelectToolClick(worldPos, _gridCol, _gridRow) {
    const monsters = mapState.monsterPlacements;
    const gridSize = mapState.gridSize;

    // First pass: check direction indicator (reverse order for z-ordering)
    for (let i = monsters.length - 1; i >= 0; i--) {
      const monster = monsters[i];
      if (!monster) continue;
      if (isPointInDirectionIndicator(worldPos, monster, gridSize)) {
        this._selectedMonsterIndex = i;
        this._isDraggingDirection = true;
        interactionState.startDirectionDrag(
          { kind: 'monster', id: monster.id },
          /** @type {import('../../types/jsdoc-types.js').TokenDirection} */ (monster.direction || 'down'),
        );
        return;
      }
    }

    // Second pass: circular hit detection for token body. Defer the
    // context-menu open until mouseup — this might turn into a drag.
    const hitIdx = findMonsterAtPosition(worldPos, monsters, gridSize);
    if (hitIdx !== null) {
      this._selectedMonsterIndex = hitIdx;
      this._draggingPlacementIndex = hitIdx;
      this._draggingPlacementStartScreen = { x: this._lastMouseX, y: this._lastMouseY };
      this._draggingPlacementPreview = null;
      this._draggingPlacementStarted = false;
      return;
    }

    // Clicked on empty space - deselect
    this._selectedMonsterIndex = null;
    this._monsterContextMenu = { visible: false, x: 0, y: 0, monsterIndex: null };
  }

  /**
   * @param {{x: number, y: number}} worldPos
   * @param {number} _gridCol
   * @param {number} _gridRow
   */
  _handleWallToolClick(worldPos, _gridCol, _gridRow) {
    if (this._wallSubtool === 'selectWall') {
      if (this._hoveredWallIndex !== null) {
        this._selectedWallIndex = this._hoveredWallIndex;
        this._currentWall = null;
        this._wallPreviewPoint = null;
      } else {
        this._selectedWallIndex = null;
      }
      return;
    }

    if (this._wallSubtool === 'drawWall') {
      const gridPoint = this._snapToGridIntersection(worldPos);

      if (!this._currentWall) {
        this._currentWall = [gridPoint];
      } else {
        // Add point if not duplicate of last point
        const lastPoint = this._currentWall[this._currentWall.length - 1];
        if (lastPoint && (lastPoint.x !== gridPoint.x || lastPoint.y !== gridPoint.y)) {
          this._currentWall = [...this._currentWall, gridPoint];
        }
      }
    }
  }

  /**
   * @param {{x: number, y: number}} _worldPos
   * @param {number} _gridCol
   * @param {number} _gridRow
   */
  async _handleOpeningToolClick(_worldPos, _gridCol, _gridRow) {
    // Selection mode
    if (this._openingSubtool === 'selectOpening') {
      if (this._hoveredOpeningIndex !== null) {
        this._selectedOpeningIndex = this._hoveredOpeningIndex;
      } else {
        this._selectedOpeningIndex = null;
      }
      return;
    }

    // Deselect if something was selected
    if (this._selectedOpeningIndex !== null && this._hoveredOpeningIndex === null) {
      this._selectedOpeningIndex = null;
    }

    // Place opening on hovered wall segment
    if (this._hoveredWallSegment && this.saveAction) {
      const segment = this._hoveredWallSegment;
      const gridSize = mapState.gridSize;

      // Determine opening properties based on subtool
      /** @type {string} */
      let openingType;
      /** @type {boolean} */
      let blocksMovement;
      /** @type {boolean} */
      let blocksVision;
      /** @type {boolean|undefined} */
      let isOpen;

      if (this._openingSubtool === 'drawDoor') {
        openingType = 'door';
        blocksMovement = true;
        blocksVision = true;
        isOpen = false;
      } else if (this._openingSubtool === 'drawWindow') {
        openingType = 'window';
        blocksMovement = true;
        blocksVision = false;
        isOpen = undefined;
      } else {
        // drawOpening
        openingType = 'opening';
        blocksMovement = false;
        blocksVision = false;
        isOpen = undefined;
      }

      // Convert pixel coordinates to grid coordinates
      const startGrid = {
        x: Math.round(segment.start.x / gridSize),
        y: Math.round(segment.start.y / gridSize),
      };
      const endGrid = {
        x: Math.round(segment.end.x / gridSize),
        y: Math.round(segment.end.y / gridSize),
      };

      /** @type {Record<string, *>} */
      const data = {
        mapId: this.mapId,
        points: [startGrid, endGrid],
        type: openingType,
        isComplete: true,
        blocksMovement,
        blocksVision,
      };

      if (isOpen !== undefined) {
        data.isOpen = isOpen;
      }

      const event = await this.saveAction({
        type: 'opening:add',
        data,
      });
      if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
    }
  }

  /**
   * @param {{x: number, y: number}} _worldPos
   * @param {number} gridCol
   * @param {number} gridRow
   */
  async _handleLightToolClick(_worldPos, gridCol, gridRow) {
    if (this._lightSubtool === 'selectLight') {
      if (this._hoveredLightIndex !== null) {
        this._selectedLightIndex = this._hoveredLightIndex;
      } else {
        this._selectedLightIndex = null;
      }
      return;
    }

    if (this._lightSubtool === 'drawLight' && this.saveAction) {
      const event = await this.saveAction({
        type: 'light:add',
        data: {
          mapId: this.mapId,
          col: gridCol,
          row: gridRow,
          radius: 4,
          intensity: 'bright',
          color: '#ffffff',
          brightRadius: 4,
          dimRadius: 4,
          isOn: true,
        },
      });
      if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
    }
  }

  /**
   * @param {{x: number, y: number}} worldPos
   * @param {number} _gridCol
   * @param {number} _gridRow
   */
  async _handleCoverToolClick(worldPos, _gridCol, _gridRow) {
    if (this._coverSubtool === 'selectCover') {
      if (this._hoveredCoverIndex !== null) {
        this._selectedCoverIndex = this._hoveredCoverIndex;
        this._currentCover = null;
        this._coverPreviewPoint = null;
      } else {
        this._selectedCoverIndex = null;
      }
      return;
    }

    // Deselect if something was selected
    if (this._selectedCoverIndex !== null && !this._currentCover) {
      this._selectedCoverIndex = null;
    }

    if (this._coverSubtool === 'drawCover') {
      const gridSize = mapState.gridSize;
      const point = this._snapToGridIntersection(worldPos);
      // Convert to pixel coords for preview rendering
      const pixelPoint = { x: point.x * gridSize, y: point.y * gridSize };

      if (!this._currentCover) {
        this._currentCover = [pixelPoint];
      } else if (this._currentCover.length >= 3) {
        // Check if clicking near first point to close polygon
        const firstPoint = this._currentCover[0];
        if (!firstPoint) return;
        const dx = Math.abs(pixelPoint.x - firstPoint.x);
        const dy = Math.abs(pixelPoint.y - firstPoint.y);
        const closeThreshold = gridSize * 0.3;

        if (dx < closeThreshold && dy < closeThreshold) {
          // Close the polygon - convert pixel points to grid points
          const allPoints = [...this._currentCover, pixelPoint];
          const gridPoints = allPoints.map((p) => ({
            x: Math.round(p.x / gridSize),
            y: Math.round(p.y / gridSize),
          }));

          if (this.saveAction) {
            const event = await this.saveAction({
              type: 'cover:add',
              data: {
                mapId: this.mapId,
                points: gridPoints,
                isComplete: true,
              },
            });
            if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
          }
          this._currentCover = null;
          this._coverPreviewPoint = null;
        } else {
          // Add another point
          this._currentCover = [...this._currentCover, pixelPoint];
        }
      } else {
        // Add point to current cover
        this._currentCover = [...this._currentCover, pixelPoint];
      }
    }
  }

  /**
   * @param {number} col
   * @param {number} row
   */
  async _handlePCStartingPositionClick(col, row) {
    const positions = mapState.pcStartingPositions || [];

    // Check if this position is already selected
    const existingIndex = positions.findIndex((/** @type {*} */ pos) => pos.col === col && pos.row === row);

    /** @type {Array<{col: number, row: number}>} */
    let newPositions;

    if (existingIndex !== -1) {
      // Remove existing position
      newPositions = /** @type {Array<{col: number, row: number}>} */ (
        positions.filter((/** @type {*} */ _, /** @type {number} */ i) => i !== existingIndex)
      );
    } else {
      // Check max 6
      if (positions.length >= 6) return;

      // Check adjacency (if positions exist)
      if (positions.length > 0) {
        const isAdjacent = positions.some((/** @type {*} */ pos) => {
          const colDiff = Math.abs(col - (pos.col ?? 0));
          const rowDiff = Math.abs(row - (pos.row ?? 0));
          return colDiff <= 1 && rowDiff <= 1 && !(colDiff === 0 && rowDiff === 0);
        });
        if (!isAdjacent) return;
      }

      newPositions = [.../** @type {Array<{col: number, row: number}>} */ (positions), { col, row }];
    }

    if (this.saveAction) {
      const event = await this.saveAction({
        type: 'pc-starting-positions:set',
        data: { mapId: this.mapId, positions: newPositions },
      });
      if (event) {
        await this._gameEvents.eventProcessor?.applyEvent(event);
      }
    }
  }

  /**
   * @param {Array<{x: number, y: number}>} points
   */
  async _finishDrawingWall(points) {
    if (points.length >= 2 && this.saveAction) {
      const event = await this.saveAction({
        type: 'wall:add',
        data: { mapId: this.mapId, points, type: 'full', blocksMovement: true, blocksLight: true, blocksVision: true },
      });
      if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
    }
    this._currentWall = null;
    this._wallPreviewPoint = null;
  }

  _cancelCurrentDrawing() {
    this._currentWall = null;
    this._wallPreviewPoint = null;
    this._currentOpening = null;
    this._hoveredWallSegment = null;
    this._currentLight = null;
    this._lightPreviewPoint = null;
    this._currentCover = null;
    this._coverPreviewPoint = null;
    this._isDraggingNewMonster = false;
    this._monsterDragPreview = null;
    this._preloadedDragImage = null;
    this._selectedMonsterIndex = null;
    this._monsterContextMenu = { visible: false, x: 0, y: 0, monsterIndex: null };
    if (this._isDraggingDirection) {
      interactionState.endDirectionDrag();
      this._isDraggingDirection = false;
      this._showRotateCursor = false;
      if (this._canvas) this._canvas.style.cursor = '';
    }
  }

  /** @param {CustomEvent} e */
  _handleStartMonsterDrag(e) {
    const monster = e.detail;
    // Preload the image
    loadImage(monster.tokenImageUrl);
    this._preloadedDragImage = null;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      this._preloadedDragImage = img;
    };
    img.src = monster.tokenImageUrl;

    this._isDraggingNewMonster = true;
    this._monsterDragPreview = {
      monster: {
        monsterSlug: monster.slug,
        name: monster.name,
        imageUrl: monster.tokenImageUrl,
        tokenSlug: monster.tokenSlug,
        borderColor: monster.tokenBorderColor,
        size: monster.tokenSize,
      },
      position: { x: 0, y: 0 },
      canPlace: true,
    };
  }

  /** @param {string} size */
  _getMonsterSizeMultiplier(size) {
    switch (size) {
      case 'tiny':
        return 0.5;
      case 'small':
        return 1.0;
      case 'medium':
        return 1.0;
      case 'large':
        return 2.0;
      case 'huge':
        return 3.0;
      case 'gargantuan':
        return 4.0;
      default:
        return 1.0;
    }
  }

  _closeAllPanels() {
    this._isWallPanelOpen = false;
    this._isOpeningPanelOpen = false;
    this._isLightPanelOpen = false;
    this._isMapPanelOpen = false;
    this._monsterPropertiesPanelVisible = false;
  }

  // UI handlers
  _handleToggleSelectMode() {
    this._tool = 'select';
  }

  _handleTogglePanMode() {
    this._tool = this._tool === 'pan' ? 'select' : 'pan';
  }

  _handleToggleDrawWallMode() {
    this._tool = this._tool === 'drawWall' ? 'select' : 'drawWall';
  }

  _handleToggleDrawOpeningMode() {
    this._tool = this._tool === 'drawOpening' ? 'select' : 'drawOpening';
  }

  _handleToggleDrawLightMode() {
    this._tool = this._tool === 'drawLight' ? 'select' : 'drawLight';
  }

  _handleToggleDrawCoverMode() {
    this._tool = this._tool === 'drawCover' ? 'select' : 'drawCover';
  }

  _handleTogglePCStartingPositionsMode() {
    this._tool = this._tool === 'setPCStartingPositions' ? 'select' : 'setPCStartingPositions';
  }

  async _handleToggleWallBlocksVision() {
    if (this._selectedWallIndex === null) return;
    const wall = mapState.walls[this._selectedWallIndex];
    if (!wall || !this.saveAction) return;
    const event = await this.saveAction({
      type: 'wall:update',
      data: { mapId: this.mapId, id: wall.id, blocksVision: !wall.blocksVision },
    });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
  }

  async _handleToggleOpeningBlocksMovement() {
    if (this._selectedOpeningIndex === null) return;
    const opening = mapState.openings[this._selectedOpeningIndex];
    if (!opening || !this.saveAction) return;
    const event = await this.saveAction({
      type: 'opening:update',
      data: { mapId: this.mapId, id: opening.id, blocksMovement: !opening.blocksMovement },
    });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
  }

  async _handleToggleOpeningBlocksVision() {
    if (this._selectedOpeningIndex === null) return;
    const opening = mapState.openings[this._selectedOpeningIndex];
    if (!opening || !this.saveAction) return;
    const event = await this.saveAction({
      type: 'opening:update',
      data: { mapId: this.mapId, id: opening.id, blocksVision: !opening.blocksVision },
    });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
  }

  async _handleToggleOpeningIsOpen() {
    if (this._selectedOpeningIndex === null) return;
    const opening = mapState.openings[this._selectedOpeningIndex];
    if (!opening || !this.saveAction) return;
    const event = await this.saveAction({
      type: 'opening:toggle',
      data: { mapId: this.mapId, id: opening.id },
    });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
  }

  async _handleDeleteSelectedWall() {
    if (this._selectedWallIndex === null) return;
    const wall = mapState.walls[this._selectedWallIndex];
    if (!wall || !this.saveAction) return;
    const event = await this.saveAction({ type: 'wall:delete', data: { mapId: this.mapId, id: wall.id } });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
    this._selectedWallIndex = null;
  }

  async _handleDeleteSelectedOpening() {
    if (this._selectedOpeningIndex === null) return;
    const opening = mapState.openings[this._selectedOpeningIndex];
    if (!opening || !this.saveAction) return;
    const event = await this.saveAction({ type: 'opening:delete', data: { mapId: this.mapId, id: opening.id } });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
    this._selectedOpeningIndex = null;
  }

  async _handleDeleteSelectedLight() {
    if (this._selectedLightIndex === null) return;
    const light = mapState.lights[this._selectedLightIndex];
    if (!light || !this.saveAction) return;
    const event = await this.saveAction({ type: 'light:delete', data: { mapId: this.mapId, id: light.id } });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
    this._selectedLightIndex = null;
  }

  async _handleDeleteSelectedCover() {
    if (this._selectedCoverIndex === null) return;
    const cover = mapState.covers[this._selectedCoverIndex];
    if (!cover || !this.saveAction) return;
    const event = await this.saveAction({ type: 'cover:delete', data: { mapId: this.mapId, id: cover.id } });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
    this._selectedCoverIndex = null;
  }

  async _handleDeleteMonster() {
    if (this._monsterContextMenu.monsterIndex === null && this._selectedMonsterIndex === null) return;
    const monsterIndex = this._monsterContextMenu.monsterIndex ?? this._selectedMonsterIndex;
    if (monsterIndex === null) return;
    const monster = mapState.monsterPlacements[monsterIndex];
    if (!monster || !this.saveAction) return;

    const event = await this.saveAction({
      type: 'monsterPlacement:delete',
      data: { mapId: this.mapId, id: monster.id },
    });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);

    this._selectedMonsterIndex = null;
    this._monsterContextMenu = { visible: false, x: 0, y: 0, monsterIndex: null };
  }

  _handleOpenMonsterProperties() {
    if (this._monsterContextMenu.monsterIndex === null) return;
    this._selectedMonsterIndex = this._monsterContextMenu.monsterIndex;
    this._monsterPropertiesPanelVisible = true;
    this._monsterContextMenu = { visible: false, x: 0, y: 0, monsterIndex: null };
  }

  async _handleToggleMonsterGivesLight() {
    if (this._selectedMonsterIndex === null) return;
    const monster = mapState.monsterPlacements[this._selectedMonsterIndex];
    if (!monster || !this.saveAction) return;
    const newGivesLight = !monster.givesLight;
    const event = await this.saveAction({
      type: 'monsterPlacement:update',
      data: {
        mapId: this.mapId,
        id: monster.id,
        givesLight: newGivesLight,
        brightRadius: newGivesLight && !monster.brightRadius ? 2 : monster.brightRadius,
        dimRadius: newGivesLight && !monster.dimRadius ? 4 : monster.dimRadius,
      },
    });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
  }

  /** @param {CustomEvent} e */
  async _handleUpdateMinPartySize(e) {
    if (this._selectedMonsterIndex === null) return;
    const monster = mapState.monsterPlacements[this._selectedMonsterIndex];
    if (!monster || !this.saveAction) return;
    const event = await this.saveAction({
      type: 'monsterPlacement:update',
      data: {
        mapId: this.mapId,
        id: monster.id,
        minPartySize: e.detail,
      },
    });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
  }

  /** @param {CustomEvent} e */
  async _handleUpdateMonsterBrightRadius(e) {
    if (this._selectedMonsterIndex === null) return;
    const monster = mapState.monsterPlacements[this._selectedMonsterIndex];
    if (!monster || !this.saveAction) return;
    const event = await this.saveAction({
      type: 'monsterPlacement:update',
      data: {
        mapId: this.mapId,
        id: monster.id,
        givesLight: monster.givesLight || false,
        brightRadius: e.detail,
        dimRadius: monster.dimRadius,
      },
    });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
  }

  /** @param {CustomEvent} e */
  async _handleUpdateMonsterDimRadius(e) {
    if (this._selectedMonsterIndex === null) return;
    const monster = mapState.monsterPlacements[this._selectedMonsterIndex];
    if (!monster || !this.saveAction) return;
    const event = await this.saveAction({
      type: 'monsterPlacement:update',
      data: {
        mapId: this.mapId,
        id: monster.id,
        givesLight: monster.givesLight || false,
        brightRadius: monster.brightRadius,
        dimRadius: e.detail,
      },
    });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
  }

  /** @param {CustomEvent} e */
  async _handleUpdateLightBrightRadius(e) {
    if (this._selectedLightIndex === null) return;
    const light = mapState.lights[this._selectedLightIndex];
    if (!light || !this.saveAction) return;
    const value = e.detail;
    const event = await this.saveAction({
      type: 'light:update',
      data: { mapId: this.mapId, id: light.id, brightRadius: value },
    });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
  }

  /** @param {CustomEvent} e */
  async _handleUpdateLightDimRadius(e) {
    if (this._selectedLightIndex === null) return;
    const light = mapState.lights[this._selectedLightIndex];
    if (!light || !this.saveAction) return;
    const value = e.detail;
    const event = await this.saveAction({
      type: 'light:update',
      data: { mapId: this.mapId, id: light.id, dimRadius: value },
    });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
  }

  async _handleToggleLightIsOn() {
    if (this._selectedLightIndex === null) return;
    const light = mapState.lights[this._selectedLightIndex];
    if (!light || !this.saveAction) return;
    const event = await this.saveAction({
      type: 'light:update',
      data: { mapId: this.mapId, id: light.id, isOn: !light.isOn },
    });
    if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
  }

  _handleZoomIn() {
    viewState.setZoom(Math.min(4.0, viewState.zoom + 0.25));
  }

  _handleZoomOut() {
    viewState.setZoom(Math.max(0.25, viewState.zoom - 0.25));
  }

  _handleResetZoom() {
    viewState.setZoom(1.0);
  }

  _handleToggleGrid() {
    this._showGrid = !this._showGrid;
  }

  _handleToggleWalls() {
    this._showWalls = !this._showWalls;
  }

  _handleCloseDrawer() {
    this._drawerOpen = false;
    this._drawerActiveView = null;
    this._saveDrawerState();
  }

  /** @param {string} view */
  _handleToggleDrawerView(view) {
    // Editor only supports monsterLibrary view
    if (view !== 'monsterLibrary') {
      return;
    }
    if (this._drawerActiveView === view && this._drawerOpen) {
      this._drawerOpen = false;
      this._drawerActiveView = null;
    } else {
      this._drawerOpen = true;
      this._drawerActiveView = view;
    }
    this._saveDrawerState();
  }

  _saveDrawerState() {
    localStorage.setItem('d3-gamemap-editor-drawerOpen', String(this._drawerOpen));
    localStorage.setItem('d3-gamemap-editor-drawerActiveView', this._drawerActiveView || '');
  }

  _handleExitPCView() {
    this._viewingAsPC = null;
  }

  /** @param {CustomEvent} e */
  async _handleSaveMapSettings(e) {
    const newIllumination = e.detail.globalIllumination;
    if (newIllumination !== mapState.globalIllumination && this.saveAction) {
      const event = await this.saveAction({
        type: 'map-settings:update',
        data: {
          mapId: this.mapId,
          globalIllumination: newIllumination,
        },
      });
      if (event) await this._gameEvents.eventProcessor?.applyEvent(event);
    }
    this._isMapPanelOpen = false;
  }

  // Icon renderers
  _renderSelectIcon() {
    return html`<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="8" r="5" />
      <path d="M20 21a8 8 0 0 0-16 0" />
    </svg>`;
  }

  _renderPanIcon() {
    return html`<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2" />
      <path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2" />
      <path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8" />
      <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
    </svg>`;
  }

  _renderWallIcon() {
    return html`<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M3 9h18" />
      <path d="M3 15h18" />
      <path d="M9 3v18" />
      <path d="M15 3v18" />
    </svg>`;
  }

  _renderDoorIcon() {
    return html`<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M18 20V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14" />
      <path d="M2 20h20" />
      <path d="M14 12v.01" />
    </svg>`;
  }

  _renderLightIcon() {
    return html`<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path
        d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"
      />
    </svg>`;
  }

  _renderCoverIcon() {
    return html`<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
      <path
        d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
      />
    </svg>`;
  }

  _renderMapPinIcon() {
    return html`<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M18 8c0 3.613-3.869 7.429-5.393 8.795a1 1 0 0 1-1.214 0C9.87 15.429 6 11.613 6 8a6 6 0 0 1 12 0" />
      <circle cx="12" cy="8" r="2" />
      <path
        d="M8.714 14h-3.71a1 1 0 0 0-.948.683l-2.004 6A1 1 0 0 0 3 22h18a1 1 0 0 0 .948-1.316l-2-6a1 1 0 0 0-.949-.684h-3.712"
      />
    </svg>`;
  }

  _renderSettingsIcon() {
    return html`<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path
        d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
      />
      <circle cx="12" cy="12" r="3" />
    </svg>`;
  }

  // Overlay icons for subtool buttons
  _renderPointerOverlay() {
    return html`<svg
      slot="overlay"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="3"
    >
      <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
    </svg>`;
  }

  _renderPlusOverlay() {
    return html`<svg
      slot="overlay"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="3"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12h8" />
      <path d="M12 8v8" />
    </svg>`;
  }

  _renderPencilOverlay() {
    return html`<svg
      slot="overlay"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="3"
    >
      <path
        d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"
      />
    </svg>`;
  }

  _renderTrashOverlay() {
    return html`<svg
      slot="overlay"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="3"
    >
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    </svg>`;
  }

  _renderEyeOverlay() {
    return html`<svg
      slot="overlay"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="3"
    >
      <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
      <circle cx="12" cy="12" r="3" />
    </svg>`;
  }

  _renderWindowIcon() {
    return html`<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M3 12h18" />
      <path d="M12 3v18" />
    </svg>`;
  }

  _renderCircleIcon() {
    return html`<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10" />
    </svg>`;
  }

  _renderMonsterContextMenu() {
    if (!this._monsterContextMenu.visible || this._monsterContextMenu.monsterIndex === null) return '';
    if (this._tool !== 'select') return '';

    const monster = mapState.monsterPlacements[this._monsterContextMenu.monsterIndex];
    if (!monster) return '';

    const gridSize = mapState.gridSize;
    const sizeMultiplier = this._getMonsterSizeMultiplier(monster.size || 'medium');
    const menuX = (monster.col * gridSize + (gridSize * sizeMultiplier) / 2) * viewState.zoom + viewState.panX;
    const menuY = monster.row * gridSize * viewState.zoom + viewState.panY - 8;

    return html`
      <div class="monster-context-menu" style="left: ${menuX}px; top: ${menuY}px;">
        <button title="Delete Monster" style="color: #ef4444" @click="${this._handleDeleteMonster}">
          ${trashIcon}
        </button>
        <button title="Monster Properties" @click="${this._handleOpenMonsterProperties}">
          ${settingsIcon}
        </button>
      </div>
    `;
  }

  _getCanvasClass() {
    let cls = 'map-editor';
    if (this._tool === 'select') cls += ' mode-select';
    if (this._tool === 'pan') cls += ' mode-pan';
    if (this._tool === 'setPCStartingPositions') cls += ' mode-select';
    // Subtool-based cursors: select subtools use default cursor, draw subtools keep crosshair (default)
    if (this._tool === 'drawWall' && this._wallSubtool === 'selectWall') cls += ' mode-select';
    if (this._tool === 'drawOpening' && this._openingSubtool === 'selectOpening') cls += ' mode-select';
    if (this._tool === 'drawLight' && (this._lightSubtool === 'selectLight' || this._lightSubtool === 'previewLights'))
      cls += ' mode-select';
    if (this._tool === 'drawCover' && this._coverSubtool === 'selectCover') cls += ' mode-select';
    if (this._isPanning) cls += ' is-panning';
    return cls;
  }

  render() {
    const showTopBanners = this._debugMode || this._viewingAsPC || this._tool === 'setPCStartingPositions';

    return html`
      <div class="map-editor-wrapper">
        <div class="map-editor-container">
          <canvas
            class="${this._getCanvasClass()}"
            width="${this._canvasWidth}"
            height="${this._canvasHeight}"
          ></canvas>

          ${
            this._eventsLoading
              ? html`
                <div class="loading-overlay">
                  <div class="loading-spinner"></div>
                  <div class="loading-text">Loading map state...</div>
                </div>
              `
              : ''
          }
          ${
            showTopBanners
              ? html`
                <div class="top-banners-container">
                  ${
                    this._debugMode
                      ? html`
                        <div class="debug-banner">
                          Grid:
                          ${
                            this._mouseGridPosition
                              ? `(${this._mouseGridPosition.col}, ${this._mouseGridPosition.row})`
                              : 'N/A'
                          }
                        </div>
                      `
                      : ''
                  }
                  ${
                    this._tool === 'setPCStartingPositions'
                      ? html`
                        <div class="pc-starting-positions-banner">
                          ${this._renderMapPinIcon()}
                          <span
                            >Select six adjacent squares for PC starting positions
                            (${mapState.pcStartingPositions?.length || 0}/6 selected)</span
                          >
                        </div>
                      `
                      : ''
                  }
                  ${
                    this._viewingAsPC
                      ? html`
                        <div class="exit-pc-view-banner">
                          <span class="viewing-text"
                            >Viewing as
                            ${mapState.playerCharacters?.find((p) => p.id === this._viewingAsPC)?.name}</span
                          >
                          <button class="exit-pc-view-button" @click="${this._handleExitPCView}">Exit PC View</button>
                        </div>
                      `
                      : ''
                  }
                </div>
              `
              : ''
          }

          <!-- Tools Sidebar -->
          <div class="tools-sidebar">
            <div class="tool-buttons-container">
              <div class="tool-buttons">
                <toolbar-button
                  ?active="${this._tool === 'select'}"
                  tooltip="Select Tool"
                  @click="${this._handleToggleSelectMode}"
                  >${this._renderSelectIcon()}</toolbar-button
                >
                <toolbar-button
                  ?active="${this._tool === 'pan' || this._isPanning}"
                  tooltip="Pan Tool"
                  @click="${this._handleTogglePanMode}"
                  >${this._renderPanIcon()}</toolbar-button
                >
                <div class="tool-separator"></div>
                <toolbar-button tooltip="Map Settings" @click="${() => (this._isMapPanelOpen = !this._isMapPanelOpen)}"
                  >${this._renderSettingsIcon()}</toolbar-button
                >
                <toolbar-button
                  ?active="${this._tool === 'setPCStartingPositions'}"
                  tooltip="PC Starting Positions"
                  @click="${this._handleTogglePCStartingPositionsMode}"
                  >${this._renderMapPinIcon()}</toolbar-button
                >
                <toolbar-button
                  ?active="${this._tool === 'drawWall'}"
                  tooltip="Walls Tools"
                  @click="${this._handleToggleDrawWallMode}"
                  >${this._renderWallIcon()}</toolbar-button
                >
                <toolbar-button
                  ?active="${this._tool === 'drawOpening'}"
                  tooltip="Openings Tools"
                  @click="${this._handleToggleDrawOpeningMode}"
                  >${this._renderDoorIcon()}</toolbar-button
                >
                <toolbar-button
                  ?active="${this._tool === 'drawLight'}"
                  tooltip="Lights Tools"
                  @click="${this._handleToggleDrawLightMode}"
                  >${this._renderLightIcon()}</toolbar-button
                >
                <toolbar-button
                  ?active="${this._tool === 'drawCover'}"
                  tooltip="Cover Tools"
                  @click="${this._handleToggleDrawCoverMode}"
                  >${this._renderCoverIcon()}</toolbar-button
                >
              </div>

              <!-- Wall Subtools -->
              ${
                this._tool === 'drawWall'
                  ? html`
                    <div class="tool-buttons">
                      <toolbar-button
                        ?active="${this._wallSubtool === 'selectWall'}"
                        tooltip="Select Wall"
                        @click="${() => (this._wallSubtool = 'selectWall')}"
                        >${this._renderWallIcon()}${this._renderPointerOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?active="${this._wallSubtool === 'drawWall'}"
                        tooltip="Draw Wall"
                        @click="${() => (this._wallSubtool = 'drawWall')}"
                        >${this._renderWallIcon()}${this._renderPlusOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?disabled="${this._selectedWallIndex === null}"
                        tooltip="Edit Wall"
                        @click="${() => (this._isWallPanelOpen = !this._isWallPanelOpen)}"
                        >${this._renderWallIcon()}${this._renderPencilOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?disabled="${this._selectedWallIndex === null}"
                        tooltip="Delete Wall"
                        @click="${this._handleDeleteSelectedWall}"
                        >${this._renderWallIcon()}${this._renderTrashOverlay()}</toolbar-button
                      >
                    </div>
                  `
                  : ''
              }

              <!-- Opening Subtools -->
              ${
                this._tool === 'drawOpening'
                  ? html`
                    <div class="tool-buttons">
                      <toolbar-button
                        ?active="${this._openingSubtool === 'selectOpening'}"
                        tooltip="Select Opening"
                        @click="${() => (this._openingSubtool = 'selectOpening')}"
                        >${this._renderDoorIcon()}${this._renderPointerOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?active="${this._openingSubtool === 'drawDoor'}"
                        tooltip="Draw Door"
                        @click="${() => (this._openingSubtool = 'drawDoor')}"
                        >${this._renderDoorIcon()}${this._renderPlusOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?active="${this._openingSubtool === 'drawWindow'}"
                        tooltip="Draw Window"
                        @click="${() => (this._openingSubtool = 'drawWindow')}"
                        >${this._renderWindowIcon()}${this._renderPlusOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?active="${this._openingSubtool === 'drawOpening'}"
                        tooltip="Draw Opening"
                        @click="${() => (this._openingSubtool = 'drawOpening')}"
                        >${this._renderCircleIcon()}${this._renderPlusOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?disabled="${this._selectedOpeningIndex === null}"
                        tooltip="Edit Opening"
                        @click="${() => (this._isOpeningPanelOpen = !this._isOpeningPanelOpen)}"
                        >${this._renderDoorIcon()}${this._renderPencilOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?disabled="${this._selectedOpeningIndex === null}"
                        tooltip="Delete Opening"
                        @click="${this._handleDeleteSelectedOpening}"
                        >${this._renderDoorIcon()}${this._renderTrashOverlay()}</toolbar-button
                      >
                    </div>
                  `
                  : ''
              }

              <!-- Light Subtools -->
              ${
                this._tool === 'drawLight'
                  ? html`
                    <div class="tool-buttons">
                      <toolbar-button
                        ?active="${this._lightSubtool === 'selectLight'}"
                        tooltip="Select Light"
                        @click="${() => (this._lightSubtool = 'selectLight')}"
                        >${this._renderLightIcon()}${this._renderPointerOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?active="${this._lightSubtool === 'drawLight'}"
                        tooltip="Draw Light"
                        @click="${() => (this._lightSubtool = 'drawLight')}"
                        >${this._renderLightIcon()}${this._renderPlusOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?active="${this._lightSubtool === 'previewLights'}"
                        tooltip="Preview Lights"
                        @click="${() => (this._lightSubtool = 'previewLights')}"
                        >${this._renderLightIcon()}${this._renderEyeOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?disabled="${this._selectedLightIndex === null}"
                        tooltip="Edit Light"
                        @click="${() => (this._isLightPanelOpen = !this._isLightPanelOpen)}"
                        >${this._renderLightIcon()}${this._renderPencilOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?disabled="${this._selectedLightIndex === null}"
                        tooltip="Delete Light"
                        @click="${this._handleDeleteSelectedLight}"
                        >${this._renderLightIcon()}${this._renderTrashOverlay()}</toolbar-button
                      >
                    </div>
                  `
                  : ''
              }

              <!-- Cover Subtools -->
              ${
                this._tool === 'drawCover'
                  ? html`
                    <div class="tool-buttons">
                      <toolbar-button
                        ?active="${this._coverSubtool === 'selectCover'}"
                        tooltip="Select Cover"
                        @click="${() => (this._coverSubtool = 'selectCover')}"
                        >${this._renderCoverIcon()}${this._renderPointerOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?active="${this._coverSubtool === 'drawCover'}"
                        tooltip="Draw Cover"
                        @click="${() => (this._coverSubtool = 'drawCover')}"
                        >${this._renderCoverIcon()}${this._renderPlusOverlay()}</toolbar-button
                      >
                      <toolbar-button
                        ?disabled="${this._selectedCoverIndex === null}"
                        tooltip="Delete Cover"
                        @click="${this._handleDeleteSelectedCover}"
                        >${this._renderCoverIcon()}${this._renderTrashOverlay()}</toolbar-button
                      >
                    </div>
                  `
                  : ''
              }
            </div>
          </div>

          <!-- View Controls -->
          <view-controls
            zoom="${viewState.zoom}"
            ?show-grid="${this._showGrid}"
            ?show-walls="${this._showWalls}"
            .showCenterButton="${false}"
            show-grid-toggle
            show-walls-toggle
            @zoom-in="${this._handleZoomIn}"
            @zoom-out="${this._handleZoomOut}"
            @reset-zoom="${this._handleResetZoom}"
            @toggle-grid="${this._handleToggleGrid}"
            @toggle-walls="${this._handleToggleWalls}"
          ></view-controls>

          <!-- Settings Panels -->
          ${
            this._isWallPanelOpen && this._selectedWallIndex !== null
              ? html`
                <wall-settings-panel
                  .wall="${mapState.walls[this._selectedWallIndex]}"
                  @dismiss="${() => (this._isWallPanelOpen = false)}"
                  @toggle-blocks-vision="${this._handleToggleWallBlocksVision}"
                ></wall-settings-panel>
              `
              : ''
          }
          ${
            this._isOpeningPanelOpen && this._selectedOpeningIndex !== null
              ? html`
                <opening-settings-panel
                  .opening="${mapState.openings[this._selectedOpeningIndex]}"
                  @dismiss="${() => (this._isOpeningPanelOpen = false)}"
                  @toggle-blocks-movement="${this._handleToggleOpeningBlocksMovement}"
                  @toggle-blocks-vision="${this._handleToggleOpeningBlocksVision}"
                  @toggle-is-open="${this._handleToggleOpeningIsOpen}"
                ></opening-settings-panel>
              `
              : ''
          }
          ${
            this._isLightPanelOpen && this._selectedLightIndex !== null
              ? html`
                <light-settings-panel
                  .light="${mapState.lights[this._selectedLightIndex]}"
                  @dismiss="${() => (this._isLightPanelOpen = false)}"
                  @update-bright-radius="${this._handleUpdateLightBrightRadius}"
                  @update-dim-radius="${this._handleUpdateLightDimRadius}"
                  @toggle-is-on="${this._handleToggleLightIsOn}"
                ></light-settings-panel>
              `
              : ''
          }
          ${
            this._isMapPanelOpen
              ? html`
                <map-settings-panel
                  global-illumination="${mapState.globalIllumination}"
                  @save="${this._handleSaveMapSettings}"
                  @dismiss="${() => (this._isMapPanelOpen = false)}"
                ></map-settings-panel>
              `
              : ''
          }
          ${
            this._monsterPropertiesPanelVisible && this._selectedMonsterIndex !== null
              ? html`
                <monster-properties-panel
                  .monster="${mapState.monsterPlacements[this._selectedMonsterIndex]}"
                  @dismiss="${() => {
                    this._monsterPropertiesPanelVisible = false;
                    this._selectedMonsterIndex = null;
                  }}"
                  @toggle-gives-light="${this._handleToggleMonsterGivesLight}"
                  @update-bright-radius="${this._handleUpdateMonsterBrightRadius}"
                  @update-dim-radius="${this._handleUpdateMonsterDimRadius}"
                  @update-min-party-size="${this._handleUpdateMinPartySize}"
                ></monster-properties-panel>
              `
              : ''
          }

          <!-- Monster Context Menu -->
          ${this._renderMonsterContextMenu()}

          <!-- Rotate Cursor -->
          <rotate-cursor
            ?show="${this._showRotateCursor}"
            .x="${this._rotateCursorX}"
            .y="${this._rotateCursorY}"
          ></rotate-cursor>

          <!-- Drawer Bar -->
          <drawer-bar
            ?drawer-open="${this._drawerOpen}"
            active-view="${this._drawerActiveView || ''}"
            show-monster-library
            @close-drawer="${this._handleCloseDrawer}"
            @toggle-view="${(/** @type {CustomEvent} */ e) => this._handleToggleDrawerView(e.detail)}"
          ></drawer-bar>
        </div>

        <!-- Drawer -->
        <drawer-panel ?open="${this._drawerOpen}">
          ${
            this._drawerActiveView === 'monsterLibrary' && this.fetchMonsters
              ? html`
                <div class="drawer-view">
                  <monster-library
                    .fetchMonsters="${this.fetchMonsters}"
                    @start-drag="${this._handleStartMonsterDrag}"
                  ></monster-library>
                </div>
              `
              : ''
          }
        </drawer-panel>
      </div>
    `;
  }
}

customElements.define('map-editor', MapEditor);
